import { VueConstructor } from 'vue';
import { Size } from '@front-app/components/constants';
import { TextAlignment } from '@front-app/components/inputs/alignmentButtons';
import { VideoProvider } from '@front-app/components/video/util';

export enum SectionType {
	FULL_WIDTH_LIST = 'full_width_list',
}

export enum ElementType {
	HEADING = 'heading',
	TEXT = 'rich_text',
	BUTTON = 'button',
	IMAGE = 'image',
	SURVEY = 'survey',
	VIDEO = 'video',
}

export enum CustomPageLayout {
	ONE_COLUMN = 'one_column',
	TWO_EQUAL_COLUMNS = 'two_equal_columns',
	TWO_COLUMNS_LEFT_WIDER = 'two_columns_left_wider',
	TWO_COLUMNS_RIGHT_WIDER = 'two_columns_right_wider',
}

export enum HighlightsElementPalette {
	PRIMARY = 'primary',
	SECONDARY1 = 'secondary1',
	SECONDARY2 = 'secondary2',
	SECONDARY3 = 'secondary3',
}

export enum HighlightsButtonAction {
	LINK_EXTERNAL = 'link_external',
	VIEW_EXTERNAL_VIDEO = 'view_external_video',
	DOWNLOAD_FILE_INTERNAL = 'download_file_internal',
	DOWNLOAD_FILE_EXTERNAL = 'download_file_external',
	MESSAGE_USER = 'message_user',
	REFER_COLLEAGUE_TO_GROUP = 'refer_colleague_to_group',
	SUBMIT_SUGGESTION = 'submit_suggestion',
	START_QUIZ = 'start_quiz',
	CONTENT_SEARCH = 'content_search',
	VIEW_EVENT = 'view_event',
}

export enum HighlightsButtonStyle {
	OUTLINED = 'outlined',
	FILLED = 'filled',
}

export enum SurveyProvider {
	JOTFORM = 'jotform',
	SURVEY_MONKEY = 'survey_monkey',
}

interface HighlightsContainer<T> {
	createdDateTime?: string,
	lastModifiedDateTime?: string,
	sections: T[];
	meta: HighlightsContainerMetadata;
}

export type HighlightsContainerRequest = HighlightsContainer<HighlightsSectionRequest>;
export interface HighlightsContainerResponse extends HighlightsContainer<HighlightsSectionResponse> {
	createdDateTime: string;
	lastModifiedDateTime: string;
}

interface HighlightsSection<T> {
	// A local id for NEW items
	newId?: string;
	// A UUID
	id?: string;
	// A string enum representing the type of section, this will dictate its
	// meta type. Currently there is only one section type (list)
	type: SectionType;
	// An array of elements contained in the section
	elements: T[];
}
export type HighlightsSectionRequest = HighlightsSection<AnyHighlightComponentRequest>;
export type HighlightsSectionResponse = Required<HighlightsSection<AnyHighlightComponent>>;

export type HighlightsComponentRequest<T extends ElementType, M> = {
	// A local id for NEW items
	newId?: string;
   // A UUID
	id?: string;
	// The type of component
	type: T;
	// The metadata associated with that component
	meta: M;
};

export type HighlightsComponentResponse<T extends ElementType, M> = Required<HighlightsComponentRequest<T, M>>;

export interface HighlightsContainerMetadata {
	name: string;
	width: number;
}

export interface HighlightsRichTextMetadata {
	mainText: string;
}

export interface HighlightsHeadingMetadata {
	mainText: string;
	size: Size;
	alignment: TextAlignment;
}

export interface HighlightsButtonMeta {
	mainText: string;
	action: HighlightsButtonAction;
	style: HighlightsButtonStyle;
	size: Size.LARGE | Size.MEDIUM | Size.SMALL;
	palette?: HighlightsElementPalette;
	href?: string;
	userId?: string;
	contentId?: string;
	contentSource?: string;
	groupId?: string;
	eventId?: string;
}

/**
 * Interface representing the JS object notation for the destringified
 * contentSource URLParams
 */
export interface ContentSourceObject {
	categories: string[];
}

export interface HighlightsImageMeta {
	href?: string;
	altText: string;
	src: string;
	ratioWidth: number;
	ratioHeight: number;
}

export enum HighlightsImageSize {
	BANNER_MEDIUM,
	BANNER_LARGE,
}

export interface HighlightsImageRatio {
	ratioWidth: number;
	ratioHeight: number;
}

export const HIGHLIGHTS_IMAGE_RATIOS: Record<HighlightsImageSize, HighlightsImageRatio> = {
	[HighlightsImageSize.BANNER_MEDIUM]: { ratioWidth: 16, ratioHeight: 4 },
	[HighlightsImageSize.BANNER_LARGE]: { ratioWidth: 16, ratioHeight: 9 },
};

export interface HighlightsVideoMeta {
	src: string | null;
	type: VideoProvider | null;
	videoId: string;
}

export interface HighlightsSurveyMeta {
	src: string | null;
	surveyId: string;
	type: SurveyProvider;
}

export interface DraggablePlaceholder<T> {
	type: T;
	component: VueConstructor;
}

export type AnyHighlightsMeta = HighlightsHeadingMetadata
	| HighlightsRichTextMetadata
	| HighlightsButtonMeta
	| HighlightsImageMeta
	| HighlightsSurveyMeta
	| HighlightsVideoMeta;

export type HighlightsHeadingComponent = HighlightsComponentResponse<ElementType.HEADING, HighlightsHeadingMetadata>;
export type HighlightsRichTextComponent = HighlightsComponentResponse<ElementType.TEXT, HighlightsRichTextMetadata>;
export type HighlightsButtonComponent = HighlightsComponentResponse<ElementType.BUTTON, HighlightsButtonMeta>;
export type HighlightsImageComponent = HighlightsComponentResponse<ElementType.IMAGE, HighlightsImageMeta>;
export type HighlightsSurveyComponent = HighlightsComponentResponse<ElementType.SURVEY, HighlightsSurveyMeta>;
export type HighlightsVideoComponent = HighlightsComponentResponse<ElementType.VIDEO, HighlightsVideoMeta>;

export type HighlightsHeadingComponentRequest = HighlightsComponentRequest<ElementType.HEADING, HighlightsHeadingMetadata>;
export type HighlightsRichTextComponentRequest = HighlightsComponentRequest<ElementType.TEXT, HighlightsRichTextMetadata>;
export type HighlightsImageComponentRequest = HighlightsComponentRequest<ElementType.IMAGE, HighlightsImageMeta>;
export type HighlightsButtonComponentRequest = HighlightsComponentRequest<ElementType.BUTTON, HighlightsButtonMeta>;
export type HighlightsSurveyComponentRequest = HighlightsComponentRequest<ElementType.SURVEY, HighlightsSurveyMeta>;
export type HighlightsVideoComponentRequest = HighlightsComponentRequest<ElementType.VIDEO, HighlightsVideoMeta>;

export type AnyHighlightComponent = HighlightsHeadingComponent
	| HighlightsRichTextComponent
	| HighlightsButtonComponent
	| HighlightsImageComponent
	| HighlightsSurveyComponent
	| HighlightsVideoComponent;

export type AnyHighlightComponentRequest = HighlightsHeadingComponentRequest
	| HighlightsRichTextComponentRequest
	| HighlightsButtonComponentRequest
	| HighlightsImageComponentRequest
	| HighlightsSurveyComponentRequest
	| HighlightsVideoComponentRequest;

export type AnyHighlightChildRequest = AnyHighlightComponentRequest
	| HighlightsSectionRequest;

export interface UnfilledHighlightsButtonMeta extends Omit<HighlightsButtonMeta, 'action'> {
	action: null;
}
export type UnfilledHighlightsButtonComponent = HighlightsComponentRequest<ElementType.BUTTON, UnfilledHighlightsButtonMeta>;
